/**
 * Token-Kit Developer Portal — Authentication Service (Cognito)
 *
 * Handles developer authentication via Amazon Cognito User Pool.
 *
 * Auth flow:
 *   1. Developer signs in with email + password
 *   2. If first login, Cognito forces password change (NEW_PASSWORD_REQUIRED)
 *   3. On success, Cognito returns ID + Access + Refresh tokens
 *   4. ID token is used as Bearer token for API calls
 *   5. Refresh token is used to silently renew expired tokens
 *
 * No self-sign-up — accounts are created by admins in Cognito.
 */

import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserSession,
} from 'amazon-cognito-identity-js';
import { appConfig } from '../config';
import { setTokenProvider } from './api.client';
import { api } from './api.client';
import { Developer } from '../types';

// ── Cognito Configuration ────────────────────────────────────────

const userPool = new CognitoUserPool({
  UserPoolId: appConfig.cognitoUserPoolId,
  ClientId: appConfig.cognitoClientId,
});

/** Currently authenticated Cognito user */
let currentUser: CognitoUser | null = null;

/** Current session (contains JWT tokens) */
let currentSession: CognitoUserSession | null = null;

// Register token provider for the API client
setTokenProvider(() => {
  if (!currentSession || !currentSession.isValid()) return null;
  return currentSession.getIdToken().getJwtToken();
});

// ── Types ────────────────────────────────────────────────────────

export interface AuthResult {
  success: boolean;
  needsNewPassword?: boolean;
  error?: string;
}

// ── Authentication Methods ───────────────────────────────────────

/**
 * Sign in with email and password.
 * Returns { success: true } on success, or { needsNewPassword: true }
 * if this is the developer's first login with a temp password.
 */
export function login(email: string, password: string): Promise<AuthResult> {
  return new Promise((resolve) => {
    const authDetails = new AuthenticationDetails({
      Username: email,
      Password: password,
    });

    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: userPool,
    });

    cognitoUser.authenticateUser(authDetails, {
      onSuccess: (session) => {
        currentUser = cognitoUser;
        currentSession = session;
        resolve({ success: true });
      },

      onFailure: (err) => {
        resolve({
          success: false,
          error: mapCognitoError(err),
        });
      },

      newPasswordRequired: () => {
        // Store the user reference so completeNewPassword can use it
        currentUser = cognitoUser;
        resolve({ success: true, needsNewPassword: true });
      },
    });
  });
}

/**
 * Complete the new-password challenge (first login with temp password).
 */
export function completeNewPassword(newPassword: string): Promise<AuthResult> {
  return new Promise((resolve) => {
    if (!currentUser) {
      resolve({ success: false, error: 'No active authentication session' });
      return;
    }

    currentUser.completeNewPasswordChallenge(newPassword, {}, {
      onSuccess: (session) => {
        currentSession = session;
        resolve({ success: true });
      },
      onFailure: (err) => {
        resolve({
          success: false,
          error: mapCognitoError(err),
        });
      },
    });
  });
}

/**
 * Sign out the current user.
 */
export function logout(): void {
  if (currentUser) {
    currentUser.signOut();
  }
  currentUser = null;
  currentSession = null;
}

/**
 * Get the currently authenticated developer's profile from the API.
 * Returns null if not authenticated.
 */
export async function getCurrentDeveloper(): Promise<Developer | null> {
  try {
    const session = await refreshSessionIfNeeded();
    if (!session) return null;

    const profile = await api.get<Developer>('/portal/me');
    return profile;
  } catch {
    return null;
  }
}

/**
 * Build a stub Developer from the Cognito ID token for admin users
 * who may not have a DynamoDB developer record.
 */
export function getAdminStubDeveloper(): Developer | null {
  if (!currentSession || !currentSession.isValid()) return null;
  try {
    const payload = currentSession.getIdToken().decodePayload();
    return {
      developerId: (payload['sub'] as string) || 'admin',
      email: (payload['email'] as string) || '',
      name: (payload['name'] as string) || 'Admin',
      namespace: 'Token-Kit',
      tier: 'enterprise' as const,
      rateLimit: 0,
      createdAt: new Date().toISOString(),
    };
  } catch {
    return null;
  }
}

/**
 * Check if there is a valid Cognito session (including refreshing if needed).
 */
export async function checkAuthenticated(): Promise<boolean> {
  const session = await refreshSessionIfNeeded();
  return session !== null;
}

/**
 * Get Cognito groups from the current ID token.
 */
export function getCognitoGroups(): string[] {
  if (!currentSession || !currentSession.isValid()) return [];
  try {
    const payload = currentSession.getIdToken().decodePayload();
    return (payload['cognito:groups'] as string[]) || [];
  } catch {
    return [];
  }
}

/**
 * Check if the current user is an admin.
 */
export function isAdmin(): boolean {
  return getCognitoGroups().includes('admins');
}

/**
 * Try to restore a persisted session on app load.
 * Cognito stores refresh tokens in localStorage automatically.
 */
export function restoreSession(): Promise<Developer | null> {
  return new Promise((resolve) => {
    const cognitoUser = userPool.getCurrentUser();
    if (!cognitoUser) {
      resolve(null);
      return;
    }

    cognitoUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session || !session.isValid()) {
        resolve(null);
        return;
      }

      currentUser = cognitoUser;
      currentSession = session;

      // Admin users may not have a DynamoDB developer record.
      // Check admin first to avoid a /portal/me 401 that triggers auth:expired.
      if (isAdmin()) {
        const stub = getAdminStubDeveloper();
        if (stub) return resolve(stub);
      }

      // Regular developer — fetch profile from the API
      getCurrentDeveloper()
        .then((dev) => resolve(dev))
        .catch(() => resolve(null));
    });
  });
}

// ── Internal Helpers ─────────────────────────────────────────────

/**
 * Refresh the Cognito session if it's expired.
 */
function refreshSessionIfNeeded(): Promise<CognitoUserSession | null> {
  return new Promise((resolve) => {
    if (!currentUser) {
      resolve(null);
      return;
    }

    currentUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session) {
        resolve(null);
        return;
      }
      currentSession = session;
      resolve(session);
    });
  });
}

/**
 * Map Cognito error codes to user-friendly messages.
 */
function mapCognitoError(err: { code?: string; message?: string }): string {
  switch (err.code) {
    case 'NotAuthorizedException':
      return 'Incorrect email or password.';
    case 'UserNotFoundException':
      return 'Incorrect email or password.'; // Don't leak user existence
    case 'UserNotConfirmedException':
      return 'Your account has not been confirmed.';
    case 'PasswordResetRequiredException':
      return 'You need to reset your password.';
    case 'InvalidPasswordException':
      return 'Password does not meet requirements (12+ chars, uppercase, lowercase, number, symbol).';
    case 'LimitExceededException':
      return 'Too many attempts. Please try again later.';
    case 'NetworkError':
      return 'Network error. Please check your connection.';
    default:
      return 'Authentication failed. Please try again.';
  }
}
